/**
 * Prices for scanned cards from POST /api/scanner/lookup (backend services/business/scanner-lookup.ts):
 * the feed's market value per printing + condition, the store's cash / store-credit offer for each
 * (the server's trade-pricing, the same function that prices trade lines) and the store's in-stock
 * products at their listed price. No buy or sell price is worked out here; this file only picks the
 * numbers for a line's printing and condition and adds them up.
 *
 * Money is USD cents throughout (`listedStoreCents` is the one exception: the same listing in the
 * store's currency, as the inventory screen shows it).
 *
 * Fetching: each card is its own react-query entry (cached, refetched when stale), but the entries
 * asked for in the same tick go out together, at most 50 ids per request (the route's limit), so
 * a tray of 80 cards is two requests, not 80.
 */
import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { api } from '../../utils/api'
import { useAuth } from '../../contexts/AuthContext'
import { choosePrinting, printingOptions, samePrinting } from './printings'
import type { TrayLine } from './sessionTray'

export const LOOKUP_MAX_IDS = 50

export type LookupVariant = {
  printing: string
  condition: string
  marketUsdCents: number | null
  offers: { cashUsdCents: number | null; creditUsdCents: number | null; floorApplied: boolean }
}

export type LookupStock = {
  productId: string
  printing: string
  condition: string
  availableQuantity: number
  listedUsdCents: number | null
  listedStoreCents: number | null
  priceUnavailable: boolean
}

export type LookupCard = {
  id: string
  name: string
  setName: string | null
  number: string
  imageUrl: string | null
  variants: LookupVariant[]
  store: LookupStock[]
}

export type LookupResponse = {
  currency: string
  tradeRules: { cashPercent: number | null; creditPercent: number | null }
  cards: LookupCard[]
  missing: string[]
}

/** One card's lookup, with the store-wide parts of the answer it came in. */
export type LookupEntry = {
  card: LookupCard
  /** The store's currency (for `listedStoreCents`). */
  currency: string
  /** null % = the store hasn't set that offer (Settings → Trade-ins). */
  cashPercent: number | null
  creditPercent: number | null
}

export function chunkIds(ids: readonly string[], size = LOOKUP_MAX_IDS): string[][] {
  const unique = [...new Set(ids)]
  const out: string[][] = []
  for (let i = 0; i < unique.length; i += size) out.push(unique.slice(i, i + size))
  return out
}

type Waiter = { resolve: (entry: LookupEntry | null) => void; reject: (e: unknown) => void }

/**
 * Collects the ids asked for in one tick and fetches them in requests of at most 50. A card the
 * catalogue doesn't have resolves to null; a failed request rejects every id in it (react-query
 * then retries those entries, which batch again).
 */
export function createLookupBatcher(
  fetchChunk: (ids: string[]) => Promise<LookupResponse>,
  schedule: (flush: () => void) => void = (flush) => { setTimeout(flush, 0) },
) {
  let pending = new Map<string, Waiter[]>()
  let scheduled = false

  const flush = () => {
    scheduled = false
    const batch = pending
    pending = new Map()
    for (const ids of chunkIds([...batch.keys()])) {
      fetchChunk(ids).then((res) => {
        const byId = new Map(res.cards.map((c) => [c.id, c]))
        for (const id of ids) {
          const card = byId.get(id)
          const entry: LookupEntry | null = card
            ? { card, currency: res.currency, cashPercent: res.tradeRules?.cashPercent ?? null, creditPercent: res.tradeRules?.creditPercent ?? null }
            : null
          batch.get(id)?.forEach((w) => w.resolve(entry))
        }
      }, (e) => {
        for (const id of ids) batch.get(id)?.forEach((w) => w.reject(e))
      })
    }
  }

  return {
    load(id: string): Promise<LookupEntry | null> {
      return new Promise((resolve, reject) => {
        pending.set(id, [...(pending.get(id) ?? []), { resolve, reject }])
        if (!scheduled) { scheduled = true; schedule(flush) }
      })
    },
  }
}

const batcher = createLookupBatcher(async (cardIds) => (await api.post('/scanner/lookup', { cardIds })).data?.data as LookupResponse)

export const lookupQuery = (organizationId: string, cardId: string) => ({
  queryKey: ['scanner', 'lookup', organizationId, cardId],
  queryFn: () => batcher.load(cardId),
  staleTime: 60_000,
})

/** Lookups for these cards, by card id (absent while loading; null = not in the catalogue). */
export function useScannerLookup(cardIds: readonly string[]) {
  const { user } = useAuth()
  const org = user?.organizationId ?? ''
  const ids = useMemo(() => [...new Set(cardIds)], [cardIds])
  const results = useQueries({ queries: ids.map((id) => ({ ...lookupQuery(org, id), enabled: Boolean(org) })) })
  const byId = new Map<string, LookupEntry | null>()
  let loading = false
  let failed = false
  results.forEach((r, i) => {
    if (r.data !== undefined) byId.set(ids[i], r.data)
    if (r.isLoading) loading = true
    if (r.isError && r.data === undefined) failed = true
  })
  return { byId, loading, failed }
}

/** The printings to choose from for a card in the lookup. */
export function lookupPrintings(card: LookupCard | undefined): Array<{ value: string; label: string }> {
  const options = printingOptions((card?.variants ?? []).map((v) => ({ printing: v.printing, condition: v.condition, price: v.marketUsdCents ?? 0 })))
  return options.length ? options : [{ value: 'Normal', label: 'Normal' }]
}

const sameCondition = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

export type LineQuote = {
  /** The printing the line is (its choice, or the card's first printing). */
  printing: string
  marketUsdCents: number | null
  cashUsdCents: number | null
  creditUsdCents: number | null
  floorApplied: boolean
  /** The store's in-stock copies of this printing + condition; null when it has none. */
  stock: { quantity: number; listedUsdCents: number | null; listedStoreCents: number | null } | null
}

/** The numbers for one printing + condition of a looked-up card (one copy). */
export function quoteFor(entry: LookupEntry | null | undefined, printing: string | null, condition: string): LineQuote {
  const card = entry?.card
  const chosen = choosePrinting(lookupPrintings(card), printing)
  const variant = card?.variants.find((v) => v.printing === chosen && sameCondition(v.condition, condition))
    ?? card?.variants.find((v) => samePrinting(v.printing, chosen) && sameCondition(v.condition, condition) && v.marketUsdCents)
  const rows = (card?.store ?? []).filter((s) => samePrinting(s.printing, chosen) && sameCondition(s.condition, condition))
  const quantity = rows.reduce((n, s) => n + s.availableQuantity, 0)
  const priced = rows.find((s) => !s.priceUnavailable)
  return {
    printing: chosen,
    marketUsdCents: variant?.marketUsdCents ?? null,
    cashUsdCents: variant?.offers.cashUsdCents ?? null,
    creditUsdCents: variant?.offers.creditUsdCents ?? null,
    floorApplied: Boolean(variant?.offers.floorApplied),
    stock: quantity > 0 ? { quantity, listedUsdCents: priced?.listedUsdCents ?? null, listedStoreCents: priced?.listedStoreCents ?? null } : null,
  }
}

export type TrayTotals = {
  /** Copies in the tray. */
  count: number
  marketUsdCents: number
  cashUsdCents: number
  creditUsdCents: number
  /** Copies with no market price (not counted in any total). */
  unpriced: number
  /** Copies still waiting for their lookup. */
  loading: number
}

/** Tray totals: each line's one-copy numbers × its quantity. */
export function trayTotals(lines: readonly TrayLine[], byId: ReadonlyMap<string, LookupEntry | null>): TrayTotals {
  const t: TrayTotals = { count: 0, marketUsdCents: 0, cashUsdCents: 0, creditUsdCents: 0, unpriced: 0, loading: 0 }
  for (const line of lines) {
    t.count += line.quantity
    if (!byId.has(line.cardId)) { t.loading += line.quantity; continue }
    const q = quoteFor(byId.get(line.cardId), line.printing, line.condition)
    if (!q.marketUsdCents) { t.unpriced += line.quantity; continue }
    t.marketUsdCents += q.marketUsdCents * line.quantity
    t.cashUsdCents += (q.cashUsdCents ?? 0) * line.quantity
    t.creditUsdCents += (q.creditUsdCents ?? 0) * line.quantity
  }
  return t
}
